import React from "react";
import { useState, useEffect } from "react";
import { IoMdClose } from "react-icons/io";
import { AiOutlineQuestionCircle } from "react-icons/ai"; // Icon for "Why me?"
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

const RecommendedBookCard = ({
  setIsRecommenderModalOpen,
  recommendedBook,
  historyOfRecommendBooks,
  recommendedBookLoading,
  getRecommendedBook,
}) => {
  const [isImageVisible, setIsImageVisible] = useState(false);
  const amazonSearchUrl = `https://www.amazon.com/s?k=${encodeURIComponent(
    recommendedBook.title
  )}+${encodeURIComponent(recommendedBook.author)}`;
  const hasReachedAltLimit = historyOfRecommendBooks.length === 5;

  const newRecommendationButton = (
    <Button
      disabled={recommendedBookLoading || hasReachedAltLimit}
      variant="outlined"
      sx={buttonStyle}
      onClick={() => getRecommendedBook()}
    >
      {recommendedBookLoading ? "Loading..." : "Try again"}
    </Button>
  );

  useEffect(() => {
    if (!recommendedBook.coverImage) {
      return;
    }

    setIsImageVisible(
      recommendedBook.coverImage.startsWith("data:image/jpeg;base64,") &&
        recommendedBook.coverImage.length > 100
    );
  }, [recommendedBook]);

  return (
    <div style={modalContainerStyle}>
      <div
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          display: "flex",
        }}
      >
        <Tooltip
          title={recommendedBook.reason || "No reason provided"}
          arrow
          placement="top"
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <AiOutlineQuestionCircle
              size={24}
              color="#555"
              style={{ marginRight: "10px" }}
            />{" "}
            Why me?
          </span>
        </Tooltip>
        <IconButton
          aria-label="close"
          disabled={recommendedBookLoading}
          onClick={() => setIsRecommenderModalOpen(false)}
        >
          <IoMdClose size={24} />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: "10px" }} />
      <div style={{ ...bookCardStyle}}>
        {/* Book Image */}
        <div style={{ ...leftSectionStyle, width: "100%" }}>
          {isImageVisible ? (
            <div style={bookImageContainerStyle}>
              <img
                onError={() => setIsImageVisible(false)}
                src={recommendedBook.coverImage}
                alt={recommendedBook.title}
                style={bookImageStyle}
              />
            </div>
          ) : (
            <div style={placeholderStyle}>
              <p>No Image Available</p>
            </div>
          )}
        </div>

        {/* Book Details */}
        <div style={bookDetailsStyle}>
          <div>
            <div style={bookTitleStyle}>{recommendedBook.title}</div>
            <div style={bookAuthorStyle}>{recommendedBook.author}</div>
            <div style={bookAuthorStyle}>{recommendedBook.publishedYear}</div>
          </div>
          <span>
            <Tooltip
              title={recommendedBook.summary || "No summary available"}
              arrow
              placement="right"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: "16px", // Larger font size
                    padding: "10px", // Optional: Adjust padding
                    backgroundColor: "#333", // Optional: Change background color
                    color: "#fff", // Optional: Change text color
                  },
                },
              }}
            >
              <p style={summaryStyle}>
                {recommendedBook.summary || "No summary available"}
              </p>
            </Tooltip>
          </span>

          <Button
            href={amazonSearchUrl}
            target="_blank"
            disabled={recommendedBookLoading}
            rel="noopener noreferrer"
            sx={buyNowButtonStyle}
          >
            Buy Now 
          </Button>

          <Tooltip title={hasReachedAltLimit ? "You have reached the maximum amount of recommendations for your selected books." : ""}>
          <div>
          <div style={altButtonContainerStyle}>{newRecommendationButton}</div>
          </div>
      </Tooltip>
        </div>
      </div>
     
      {recommendedBookLoading && (
        <CircularProgress size="50px" style={spinnerStyle} />
      )}
    </div>
  );
};

// Styles
const buttonStyle = {
  padding: "12px 24px",
  fontSize: "16px",
  borderRadius: "50px",
  border: "1px solid #223293",
  fontFamily: "Manrope, sans-serif",
  background: "#E9EBF6",
  color: "#223293",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#DCE6F2", // A darker version of #223293
  },
};
const spinnerStyle = {
  position: "absolute",
};

const modalContainerStyle = {
  position: "relative",
  outline: "none",
  fontFamily: "Manrope, sans-serif",
  maxHeight: "800px",
  overflow: "auto",
  backgroundColor: "#fff",
  borderRadius: "8px",
  maxWidth: "800px",
  justifyContent: "center",
  alignItems: "center",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
};

const bookCardStyle = {
  display: "flex",
  paddingBottom: "10px",
  flexDirection: "column",
  alignItems: "center",
  gap: "20px",
};

const leftSectionStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  width: "200px",
  gap: "10px",
};

const bookImageContainerStyle = {
  maxWidth: "200px",
  maxHeight: "300px",
  height: "auto",
  borderRadius: "8px",
  position: "relative",
};

const bookImageStyle = {
  width: "100%",
  borderRadius: "30px",
  objectFit: "contain",
  position: "relative",
};

const placeholderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "200px",
  height: "300px",
  textAlign: "center",
  color: "#888",
  borderRadius: "30px",
  border: "1px solid black",
  fontSize: "14px",
  fontStyle: "italic",
};

const bookDetailsStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  paddingTop: "10px",
  paddingRight: "20px",
};

const bookTitleStyle = {
  fontSize: "20px",
  fontWeight: "bold",
  color: "#1A2772",
};

const bookAuthorStyle = {
  fontSize: "14px",
  fontWeight: "bold",
  color: "#070A1D",
  marginTop: "5px",
};

const summaryStyle = {
  fontSize: "14px",
  color: "#9194A9",
  lineHeight: "1.6",
  maxHeight: '150px', // Set a fixed height
  overflow: 'auto',
  display: "-webkit-box", // Required for multi-line truncation
  WebkitBoxOrient: "vertical", // Required for multi-line truncation
  WebkitLineClamp: 8, // Limit to 3 lines
  textOverflow: "ellipsis", // Add ellipsis
  whiteSpace: "normal", // Allow text wrapping
  cursor: "pointer", // Indicate interactivity for tooltip
};

const altButtonContainerStyle = {
  marginTop: "20px",
  fontFamily: "Manrope, sans-serif",
};

const buyNowButtonStyle = {
  backgroundColor: "#ff9900",
  color: "#fff",
  fontFamily: "Manrope, sans-serif",
  padding: "12px 20px",
  borderRadius: "50px",
  fontSize: "16px",
  cursor: "pointer",
  textTransform: "uppercase",
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: "#e68a00",
  },
};

export default RecommendedBookCard;
